import type { AlignmentStatus } from '../../types/metro';
import { useFilterStore } from '../../stores/filterStore';
import { OccupationGroupCombobox } from '../shared/OccupationGroupCombobox';
import { StatusChips } from '../shared/StatusChips';
import { OCCUPATIONAL_GROUPS } from '../../lib/occupational-groups';
import { useIsStuck } from '../../lib/use-is-stuck';
import { STICKY_OFFSET_BELOW_SECTION_NAV } from '../../lib/layout-offsets';
import { cn } from '../../lib/utils';
import { TableActions } from '../shared/TableActions';

/**
 * Sticky filter bar — sits in the natural page flow (below the page
 * header) and pins to the top below the Topbar + SectionNav
 * (top: var(--offset-nav-stack), a calc() built on the responsive
 * var(--h-topbar) -- 134px = 74 + 60 desktop, CR-028/029) once
 * the user scrolls past it. On mobile (<600px) SectionNav is hidden,
 * so the bar pins directly below the Topbar instead -- var(--h-topbar)
 * itself resolves to 68px at those widths (DEC-075 single-owner CSS
 * var, TD-08a/b), so the responsive top class is simply
 * `top-[var(--h-topbar)] min-[600px]:top-[var(--offset-nav-stack)]`.
 * BUG-002 (Maria 2026-06-01):
 * without the mobile-specific top, content scrolled through the
 * 60px gap between the Topbar bottom and the pinned bar top. Background
 * spans the full viewport; inner controls align to the 1200px page
 * grid so Search/Dropdown/Status sit directly above the table columns.
 * Elevation shadow lights up only when the bar is stuck — matches the
 * SectionNav `is-stuck` shadow treatment for visual consistency.
 *
 * Container max-width 1200px (matches the page section + table) so the
 * controls stay aligned with the rest of the page grid. Background is
 * full width via the outer sticky wrapper.
 *
 * State source: cross-page `useFilterStore` so the same search/status
 * filters apply on the Map page (and vice versa).
 */
interface RankingsFilterBarProps {
  readonly getCsvData?: () => string;
}

export function RankingsFilterBar({ getCsvData }: RankingsFilterBarProps = {}) {
  const [stickyRef, isStuck] = useIsStuck<HTMLDivElement>(STICKY_OFFSET_BELOW_SECTION_NAV);
  const statuses = useFilterStore((s) => s.statuses);
  const toggleStatus = useFilterStore((s) => s.toggleStatus);
  const occupationalGroupId = useFilterStore((s) => s.occupationalGroupId);
  const setOccupationalGroup = useFilterStore((s) => s.setOccupationalGroup);

  return (
    <div
      ref={stickyRef}
      className={cn(
        'sticky top-[var(--h-topbar)] z-[99] min-[600px]:top-[var(--offset-nav-stack)] border-b border-border bg-ice transition-shadow',
        isStuck && 'shadow-sticky',
      )}
      role="region"
      aria-label="Rankings filter bar"
    >
      <div className="mx-auto flex max-w-page flex-col gap-3 px-8 py-3 md:flex-row md:flex-wrap md:items-end md:gap-4">
        {/* PR B (combobox plan, 2026-06-01): swapped the plain
            OccupationGroupSelect (BrandSelect button + listbox) for
            the typeahead OccupationGroupCombobox so users can type
            a substring through the 22 occupational groups instead
            of scanning a panel. Matches the Detail page (PR #72) and
            now also Map + Providers. Slot bumped from 220px to 240px
            to give the typeahead input + magnifier glyph + chevron
            enough room without truncating placeholder text.
            UX (2026-06-03): bumped 240 -> 280 to match DetailFilterBar
            so the longest OG label ("Installation, maintenance, and
            equipment repair") no longer wraps inside the input. Single
            source of truth for slot width across pages is the goal. */}
        <div
          data-testid="rankings-filter-slot-og"
          className="md:w-filter-slot md:shrink-0"
        >
          <OccupationGroupCombobox
            value={occupationalGroupId ?? ''}
            onChange={(id) => setOccupationalGroup(id || null)}
            groups={OCCUPATIONAL_GROUPS}
          />
        </div>
        <div className="md:w-[360px] md:shrink-0">
          <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-text-4">
            Status
          </span>
          <StatusChips
            selected={statuses as ReadonlySet<AlignmentStatus>}
            onToggle={toggleStatus}
          />
        </div>
        <TableActions getCsvData={getCsvData} filename="georgetown-cew-rankings" />
      </div>
    </div>
  );
}
